import React, { useState, useRef } from 'react';
import { synthesizeSpeech } from '../../services/api';

// Cache for storing audio blobs to avoid re-requesting
const audioCache = new Map<string, string>();

interface WordPlayButtonProps {
  word: string;
  size?: 'sm' | 'md';
  className?: string;
}

const WordPlayButton: React.FC<WordPlayButtonProps> = ({ word, size = 'sm', className = '' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Normalize word for cache key (lowercase, trimmed)
  const cacheKey = word.toLowerCase().trim();

  const handlePlay = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent click handlers

    // If already playing, stop
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      return;
    }

    // Clean up previous audio element
    if (audioRef.current) {
      audioRef.current.pause();
    }

    // Check cache first
    const cachedUrl = audioCache.get(cacheKey);
    if (cachedUrl) {
      const audio = new Audio(cachedUrl);
      audioRef.current = audio;

      audio.onended = () => setIsPlaying(false);
      audio.onerror = () => setIsPlaying(false);

      await audio.play();
      setIsPlaying(true);
      return;
    }

    // Not in cache, fetch from API
    setIsLoading(true);
    try {
      const audioBlob = await synthesizeSpeech(word);
      const audioUrl = URL.createObjectURL(audioBlob);

      // Store in cache
      audioCache.set(cacheKey, audioUrl);

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => setIsPlaying(false);
      audio.onerror = () => setIsPlaying(false);

      await audio.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('Failed to play word pronunciation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sizeClasses = size === 'sm'
    ? 'w-6 h-6 p-1'
    : 'w-8 h-8 p-1.5';

  return (
    <button
      onClick={handlePlay}
      disabled={isLoading}
      className={`
        inline-flex items-center justify-center rounded-full transition-all
        ${isPlaying
          ? 'bg-green-100 text-green-600 hover:bg-green-200'
          : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
        }
        ${isLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
        ${sizeClasses}
        ${className}
      `}
      title={isPlaying ? 'Stop' : `Listen to "${word}"`}
    >
      {isLoading ? (
        <svg className="animate-spin w-full h-full" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ) : isPlaying ? (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-full h-full" viewBox="0 0 24 24" fill="currentColor">
          <rect x="6" y="4" width="4" height="16" rx="1" />
          <rect x="14" y="4" width="4" height="16" rx="1" />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-full h-full" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5v14l11-7z" />
        </svg>
      )}
    </button>
  );
};

export default WordPlayButton;
